// src/components/validationNotes/PageFeuilleNotes.jsx
// ============================================================================
// La page d'une feuille de notes d'enseignant
// (/administration/notes/saisies/:feuilleId)
// ============================================================================
//
// Une seule page de validation par feuille, deux entrées : l'onglet « Saisies
// des enseignants » du carnet et l'onglet « Notes » de la page d'un enseignant
// dans le suivi (décision de Daniel du 7 octobre 2026) — plus la cloche, dont
// la notification mène ici. Navigation drill-in : la page se lit en entier
// avant de décider, aucune fenêtre empilée pour consulter.
//
// Ce qu'elle montre, ligne par ligne : la saisie de l'enseignant, la note que
// le carnet a AUJOURD'HUI à la place de cette épreuve, l'écart entre les deux
// (surligné), et ce que le report écrira — ou n'écrira pas : une ligne
// verrouillée dans le carnet, venue d'un autre site ou modifiée depuis le
// dernier report est un CONFLIT, jamais écrasée — sauf quand elle porte déjà
// la note de la saisie : rien à écrire, ce n'est pas un conflit (« Déjà au
// carnet »), et la feuille peut se refermer. Cette prévision vient du
// serveur, qui applique la même règle pour annoncer et pour écrire : la page
// annonce exactement ce que la validation fera. Puis la demande de
// rectification et son justificatif, s'il y en a une, et le journal.
//
// Ce qu'elle laisse faire : ce que le serveur annonce dans `actions`, calculé
// pour CET employé — droit (NOTES.VALIDATION.VALIDER / RECTIFIER), statut de
// la feuille, année ouverte, et jamais celui qui a saisi la feuille :
//   - valider : report dans le carnet, NON verrouillé, à la seule position de
//     l'épreuve ; renvoyer à l'enseignant, avec un motif ;
//   - refaire le report d'une feuille validée, une fois les lignes en conflit
//     déverrouillées par l'outil du carnet ;
//   - autoriser ou refuser une rectification. Si des notes concernées sont
//     verrouillées, autoriser passe par le déverrouillage existant du carnet
//     (NOTES.VERROUILLAGE.UNLOCK, motif, historique, FORCE au-delà du délai),
//     jamais par un passe-droit.
//
// Chaque décision part avec la VERSION de la feuille affichée : une feuille
// retirée puis resoumise entre-temps n'est plus celle qu'on a lue, le serveur
// refuse (409) et la page se relit.
//
// Rien ici n'écrit dans les appréciations : moyenne générale, rangs et
// appréciations du trimestre restent calculés par le carnet, quand le
// secrétariat l'enregistre (ce qui verrouille aussi les notes reportées).
//
// Une feuille du PRIMAIRE (N3 : classe × matière × mois, notée sur 10 au
// demi-point) se lit ici de la même façon, avec ses différences :
//   - pas de bouton « Valider » : la direction valide le mois de la classe en
//     un geste, sur la page du mois (/administration/notes/saisies/primaire/
//     :classeId/:moisId), où mène un bouton ; le serveur refuse d'ailleurs la
//     validation d'une feuille du primaire seule (409 VALIDER_LE_MOIS) ;
//   - renvoyer à l'enseignant, refaire le report des élèves restés en
//     conflit, autoriser ou refuser une rectification se font ici, par
//     matière, avec les clés du primaire (NOTES.VALIDATION_PRIMAIRE.*) ;
//   - le carnet est lu au mois de la feuille, et c'est le MOIS qui est publié
//     aux familles (ou retiré), pas le trimestre.
// ============================================================================

import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  AlertCircle, CalendarCheck, ClipboardCheck, FilePen, Loader2, Lock, Paperclip, RefreshCw, Undo2, XCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import EtatVide from '../ui/EtatVide';
import { Toast } from '../ui/Toast';
import LongToast, { shouldUseLongToast } from '../ui/LongToast';
import ToastPortal from '../ToastPortal';
import ConfirmDialog from '../ConfirmDialog';
import { useFeatures } from '../../contexts/FeaturesContext';
import { usePermissions } from '../../hooks/usePermissions';
import { CarteCompteur } from '../suiviEnseignants/elementsSuivi';
import { dateHeure, estVrai, nombre, nomEleve, pluriel } from '../suiviEnseignants/formatSuivi';
import { validationNotes } from './apiValidationNotes';
import { CLES_VALIDATION, clesDuNiveau, droitsDuNiveau, useAccesValidationNotes } from './droitsValidationNotes';
import {
  BadgeStatutDemande, BadgeStatutFeuille, BoutonRetour, TONS_PREVISION, acteurJournal, adresseMois, baremeDe,
  classesFiltre, detailsJournal, ecrireNote, estConflitARegler, estEffacement, estPrimaire, intituleFeuille,
  intituleMois, lecturePrevision, libelleAction, libelleMois, lireNote, moisIdDe, nomEmploye, noteOuTiret,
  periodeFeuille, tailleFichier
} from './presentationNotes';
import VisionneusePiece from './VisionneusePiece';

// Mêmes bornes que le serveur (NotesEnseignantService : MOTIF_MIN, MOTIF_MAX),
// comptées comme lui, en caractères et non en unités UTF-16.
const MOTIF_MIN = 10;
const MOTIF_MAX = 2000;
const longueurMotif = (texte) => Array.from(texte.trim()).length;

const BOUTON_ACTION = 'h-9 min-w-[210px] justify-center gap-2';
const BOUTON_PRINCIPAL = 'bg-[#246990] text-white hover:bg-[#1f5a7a]';
// Le même bouton plein, sur un lien (vers la page du mois, au primaire).
const LIEN_PRINCIPAL = 'inline-flex h-9 min-w-[210px] items-center justify-center gap-2 whitespace-nowrap rounded-md bg-[#246990] px-4 text-sm font-medium text-white shadow transition-colors hover:bg-[#1f5a7a] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#246990]/30';

// Le chemin de l'outil de déverrouillage du carnet (ParametresVerrouillageNotes,
// dans ParametrageCarnetEvaluation), qui n'a pas d'adresse propre.
const CHEMIN_DEVERROUILLAGE = "Outils administratifs › Paramètres › Carnet d'évaluation › Déverrouillage";

// Les refus qui disent que l'écran est en retard sur la base ou sur les droits
// (feuille retirée, déjà décidée par un collègue, droit retiré, demande
// disparue, version illisible — le serveur demande alors de recharger) : on
// ferme la fenêtre et on relit. Ceux-là, au contraire, gardent la fenêtre et
// ce qui y est écrit : ils se corrigent sur place (motif trop court) ou
// demandent quelqu'un d'autre (déverrouillage), ou un instant (notes en cours
// de modification ailleurs).
// La matière sortie de la classe, ou la classe passée à un autre niveau
// (409 MATIERE_HORS_CLASSE, NIVEAU_CLASSE_CHANGE) : la page n'est pas en
// retard, la décision est impossible et le restera. Le message du serveur dit
// quoi faire (« Refusez cette demande. ») : il se lit dans la fenêtre, d'où
// l'on passe au refus (intégration N3).
const REFUS_A_LIRE_SUR_PLACE = new Set([
  'DEVERROUILLAGE_REQUIS', 'DEVERROUILLAGE_FORCE_REQUIS', 'MOTIF_TROP_COURT', 'TEXTE_TROP_LONG',
  'TEXTE_INVALIDE', 'NOTES_OCCUPEES', 'MATIERE_HORS_CLASSE', 'NIVEAU_CLASSE_CHANGE'
]);
const ecranEnRetard = (erreur) => erreur?.code === 'VERSION_REQUISE'
  || (!REFUS_A_LIRE_SUR_PLACE.has(erreur?.code) && [403, 404, 409].includes(erreur?.statut));

const CONFLITS_LISTES = 6;

const FOND_LIGNE = {
  conflit: 'bg-red-50/60',
  ecart: 'bg-amber-50/70'
};

const FILTRES = [
  { cle: 'tous', libelle: 'Tous les élèves' },
  { cle: 'ecarts', libelle: 'Différentes du carnet' },
  { cle: 'conflits', libelle: 'Conflits' },
  { cle: 'verrouillees', libelle: 'Verrouillées au carnet' },
  { cle: 'concernes', libelle: 'Rectification demandée' }
];

const ChampMotif = ({ id, valeur, onChange, libelle, aide, requis }) => {
  const court = requis && longueurMotif(valeur) < MOTIF_MIN;
  // L'aide (ou la règle des 10 caractères) est lue avec le champ : sans ce
  // lien, un lecteur d'écran ne dit pas pourquoi le bouton reste grisé.
  const idAide = `${id}-aide`;
  return (
    <div className="text-left">
      <label htmlFor={id} className="mb-1.5 block text-sm text-gray-700">
        {libelle}
        {requis && <span aria-hidden="true" className="text-red-600"> *</span>}
      </label>
      <textarea
        id={id}
        rows={4}
        maxLength={MOTIF_MAX}
        value={valeur}
        onChange={(e) => onChange(e.target.value)}
        aria-required={requis || undefined}
        aria-describedby={idAide}
        className="w-full resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800 outline-none transition focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
      />
      <p className="mt-1 flex justify-between gap-3 text-[11px] text-gray-500">
        <span id={idAide}>{court ? `Au moins ${MOTIF_MIN} caractères.` : aide}</span>
        <span className="tabular-nums">{valeur.length} / {MOTIF_MAX}</span>
      </p>
    </div>
  );
};

/** Une liste « nom — valeur » repliée, pour les photographies du journal. */
const ValeursRepliees = ({ titre, lignes }) => (
  <details className="mt-1.5 text-xs">
    <summary className="cursor-pointer font-semibold text-[#246990]">{titre}</summary>
    <ul className="mt-1.5 space-y-0.5">
      {lignes.map((l) => (
        <li key={l.cle} className="flex justify-between gap-3 text-gray-700">
          <span className="truncate">{l.nom}</span>
          <span className={`shrink-0 tabular-nums ${l.ton || ''}`}>{l.valeur}</span>
        </li>
      ))}
    </ul>
  </details>
);

const PageFeuilleNotes = () => {
  const { feuilleId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { estActive } = useFeatures();
  const { hasPermission } = usePermissions();
  const { aLaCle } = useAccesValidationNotes();
  const idMotif = useId();

  const [donnees, setDonnees] = useState(null);
  const [chargement, setChargement] = useState(false);
  // `{ message, statut }` : le statut dit si « Réessayer » a un sens (pas pour
  // une feuille introuvable ni un droit refusé).
  const [erreur, setErreur] = useState(null);
  const [toast, setToast] = useState(null);
  const [filtre, setFiltre] = useState('tous');
  // `{ type: 'valider'|'reporter'|'renvoyer'|'autoriser'|'refuser', demande? }`
  const [dialogue, setDialogue] = useState(null);
  const [motif, setMotif] = useState('');
  const [erreurDialogue, setErreurDialogue] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const [pieceOuverte, setPieceOuverte] = useState(null);

  // Seule la réponse de la DERNIÈRE demande s'affiche (relecture après une
  // décision pendant qu'une autre lecture est en route).
  const demandeEnCours = useRef(0);
  // Verrou immédiat : `envoi` ne désactive le bouton qu'au rendu suivant ; un
  // second clic arrivé avant enverrait une seconde validation, que le serveur
  // refuserait (409) — un message d'erreur juste après le succès.
  const actionEnCours = useRef(false);

  const charger = useCallback(async () => {
    const numero = ++demandeEnCours.current;
    setChargement(true);
    setErreur(null);
    try {
      const reponse = await validationNotes.feuille(feuilleId);
      if (numero === demandeEnCours.current) setDonnees(reponse.data);
    } catch (e) {
      if (numero === demandeEnCours.current) setErreur({ message: e.message, statut: e.statut || null });
    } finally {
      if (numero === demandeEnCours.current) setChargement(false);
    }
  }, [feuilleId]);

  // Une autre feuille : on repart de zéro, sans montrer un instant la précédente.
  useEffect(() => {
    setDonnees(null);
    setFiltre('tous');
  }, [feuilleId]);
  useEffect(() => { charger(); }, [charger]);

  // ---------------------------------------------------------------------------
  // Ce que dit la feuille
  // ---------------------------------------------------------------------------

  const feuille = useMemo(() => donnees?.feuille || {}, [donnees]);
  const eleves = useMemo(() => (Array.isArray(donnees?.eleves) ? donnees.eleves : []), [donnees]);
  const demandes = useMemo(() => (Array.isArray(donnees?.demandes) ? donnees.demandes : []), [donnees]);
  const journal = Array.isArray(donnees?.journal) ? donnees.journal : [];
  // Le niveau de la feuille : il choisit les clés (NOTES.VALIDATION.* ou
  // NOTES.VALIDATION_PRIMAIRE.*, jamais l'une pour l'autre), le barème, et ce
  // que l'espace famille publie (le mois au primaire, le trimestre au-delà).
  const primaire = estPrimaire(feuille);
  const niveau = primaire ? 'primaire' : 'secondaire';
  const cles = clesDuNiveau(niveau);
  const bareme = baremeDe(feuille, primaire);
  const moisId = moisIdDe(feuille);
  const periodePubliee = primaire ? 'mois' : 'trimestre';
  const actions = donnees?.actions || {};
  const droits = droitsDuNiveau(donnees?.droits, niveau);
  const compteurs = donnees?.compteurs || {};
  const brouillon = estVrai(feuille.brouillon) || feuille.statut === 'brouillon';
  const anneeOuverte = feuille.annee ? estVrai(feuille.annee.ouverte) : true;

  const nomsParEleve = useMemo(() => new Map(eleves.map((e) => [String(e.id), nomEleve(e)])), [eleves]);
  const nomDe = useCallback(
    (id) => nomsParEleve.get(String(id)) || `Élève n° ${id}`,
    [nomsParEleve]
  );

  const demandeEnAttente = demandes.find((d) => d.statut === 'en_attente') || null;
  const demandesPassees = demandes.filter((d) => d !== demandeEnAttente);
  const concernes = useMemo(
    () => new Set((demandeEnAttente?.eleves || []).map(String)),
    [demandeEnAttente]
  );
  // Les élèves rouverts par la dernière rectification autorisée : tant que la
  // feuille est chez l'enseignant, ou revenue soumise après une validation.
  const rouverts = useMemo(() => {
    const enRectification = feuille.statut === 'rectification_autorisee'
      || (feuille.statut === 'soumise' && Boolean(feuille.validee_le));
    const derniere = enRectification ? demandes.find((d) => d.statut === 'autorisee') : null;
    return new Set((derniere?.eleves || []).map(String));
  }, [demandes, feuille.statut, feuille.validee_le]);

  // Un conflit « à régler » : le carnet diffère et le report n'y touchera pas.
  // Une ligne verrouillée qui porte déjà la note n'en est pas un : le serveur
  // la dit « identique ».
  const estConflit = estConflitARegler;
  const estVerrouillee = (e) => estVrai(e.carnet?.verrouille);
  // Les lignes qu'autoriser la rectification déverrouillera : la règle du
  // serveur (`autoriserRectification`), élèves encore dans la classe, ligne
  // verrouillée et NON transférée — une ligne venue d'un autre site n'est
  // jamais réécrite, le serveur ne la déverrouille pas. La compter ici
  // exigeait un motif et le droit de déverrouiller pour rien, et grisait
  // « Autoriser » chez qui n'a pas ce droit alors que le serveur l'accepte.
  const verrouilleesConcernees = eleves.filter((e) => concernes.has(String(e.id)) && estVerrouillee(e)
    && !estVrai(e.carnet?.transferee) && e.dans_classe !== false);
  // Les élèves de la demande en attente qui ne sont plus dans la classe
  // (partis depuis la demande) : l'autorisation serait sans effet pour eux, le
  // serveur la refuse (ELEVE_SORTI). Un élève sans ligne sur la feuille et
  // parti n'est même plus dans la grille : il compte aussi.
  const concernesSortis = [...concernes].filter(
    (id) => !eleves.some((e) => String(e.id) === id && e.dans_classe !== false)
  );

  // Ce que le report écrira, d'après la prévision du serveur.
  const prevu = useMemo(() => {
    const c = {
      ajouts: 0, remplacements: 0, effacements: 0, inchanges: 0, deja: 0, conflits: 0,
      absents: 0, verrouillees: 0, sansSaisie: 0, parMotif: {}
    };
    eleves.forEach((e) => {
      if (estVrai(e.absent)) c.absents += 1;
      // Un élève de la classe que la saisie ne note pas (arrivé dans la classe
      // après la soumission, ou après une première validation) : la
      // validation ne lui écrit rien. C'est un écart avec la classe, à voir
      // avant de valider.
      if (e.dans_classe !== false && !estVrai(e.saisie) && !estEffacement(e)) c.sansSaisie += 1;
      const r = e.prevision?.resultat;
      if (r === 'insere') c.ajouts += 1;
      else if (r === 'mis_a_jour') {
        // Trois cas sous un même résultat du serveur : une case vide du
        // carnet remplie, une note remplacée, une note EFFACÉE (ce circuit
        // l'avait reportée, la saisie ne la porte plus). Les annoncer sous un
        // seul mot cacherait l'effacement d'une note.
        if (lireNote(e.carnet?.valeur) === null) c.ajouts += 1;
        else if (estEffacement(e)) c.effacements += 1;
        else c.remplacements += 1;
      } else if (r === 'identique' || r === 'rien_a_ecrire') c.inchanges += 1;
      else if (r === 'deja_reporte') c.deja += 1;
      else if (r === 'conflit') {
        c.conflits += 1;
        if (e.prevision.motif === 'verrouillee') c.verrouillees += 1;
        c.parMotif[e.prevision.motif] = (c.parMotif[e.prevision.motif] || 0) + 1;
      }
    });
    return c;
  }, [eleves]);

  // Un brouillon n'envoie pas ses valeurs : ni écart ni conflit à filtrer.
  const filtresProposes = FILTRES.filter((f) => {
    if (brouillon) return f.cle === 'tous' || f.cle === 'verrouillees';
    return f.cle !== 'concernes' || concernes.size > 0;
  });
  // Un filtre qui n'est plus proposé (la demande vient d'être décidée, la
  // page relue) laissait une grille vide sous une rangée sans bouton actif :
  // on revient à tous les élèves.
  const filtreActif = filtresProposes.some((f) => f.cle === filtre) ? filtre : 'tous';
  const elevesFiltres = eleves.filter((e) => {
    if (filtreActif === 'ecarts') return estVrai(e.ecart);
    if (filtreActif === 'conflits') return estConflit(e);
    if (filtreActif === 'verrouillees') return estVerrouillee(e);
    if (filtreActif === 'concernes') return concernes.has(String(e.id));
    return true;
  });

  const parentsOuverts = estActive('module.parents');
  const outilsOuverts = hasPermission('OUTILS_ADMINISTRATIFS');
  const peutDeverrouiller = estVrai(droits.deverrouiller);
  const autorisationBloquee = verrouilleesConcernees.length > 0 && !peutDeverrouiller;

  // ---------------------------------------------------------------------------
  // Retour
  // ---------------------------------------------------------------------------

  // À la file du carnet ou à la page de l'enseignant telles qu'on les a
  // laissées (filtres dans leur adresse) si l'on en vient : on recule dans
  // l'historique. Arrivée par la cloche ou par un lien : vers l'entrée que la
  // personne peut ouvrir — le carnet d'abord, sinon la page de l'enseignant
  // dans le suivi ; ni l'un ni l'autre, l'accueil.
  const depuis = location.state?.depuis;
  const carnetOuvert = hasPermission('CARNET_EVALUATION') && estActive('module.evaluations');
  // Même composition que l'entrée du menu (SidebarNav) : la porte, la clé fine
  // ET la licence du suivi — sans elle, le lien mènerait à « module non inclus ».
  const suiviOuvert = hasPermission('SUIVI_ENSEIGNANTS') && aLaCle('SUIVI_ENSEIGNANTS.VUE_GLOBALE.VIEW')
    && estActive('module.suivi_enseignants');
  const enseignantId = feuille.soumise_par?.id || feuille.auteur?.id;
  // Au primaire, la page du mois de la classe : c'est là qu'on valide.
  const adresseDuMois = primaire && feuille.classe_id && moisId ? adresseMois(feuille.classe_id, moisId) : null;
  const retour = (() => {
    if (depuis === 'mois' && adresseDuMois) {
      return { libelle: intituleMois(feuille.classe, feuille.mois || libelleMois(feuille)), adresse: adresseDuMois };
    }
    if (depuis === 'suivi' && location.state?.employeId) {
      return { libelle: "Page de l'enseignant", adresse: `/administration/enseignants/${location.state.employeId}?onglet=notes` };
    }
    if (depuis === 'carnet') {
      return { libelle: 'Saisies des enseignants', adresse: '/administration/notes?onglet=saisies' };
    }
    if (adresseDuMois) {
      return { libelle: intituleMois(feuille.classe, feuille.mois || libelleMois(feuille)), adresse: adresseDuMois };
    }
    if (carnetOuvert) {
      return { libelle: 'Saisies des enseignants', adresse: '/administration/notes?onglet=saisies' };
    }
    if (suiviOuvert && enseignantId) {
      return { libelle: "Page de l'enseignant", adresse: `/administration/enseignants/${enseignantId}?onglet=notes` };
    }
    return { libelle: 'Accueil', adresse: '/administration' };
  })();
  const revenir = (e) => {
    if (depuis) {
      e.preventDefault();
      navigate(-1);
    }
  };

  // ---------------------------------------------------------------------------
  // Décisions
  // ---------------------------------------------------------------------------

  const ouvrir = (type, demandeVisee = null) => {
    setMotif('');
    setErreurDialogue(null);
    setDialogue({ type, demande: demandeVisee });
  };

  const fermer = () => {
    if (envoi) return;
    setDialogue(null);
    setMotif('');
    setErreurDialogue(null);
  };

  const motifPret = longueurMotif(motif) >= MOTIF_MIN;
  const motifExige = dialogue?.type === 'renvoyer' || dialogue?.type === 'refuser'
    || (dialogue?.type === 'autoriser' && verrouilleesConcernees.length > 0);

  /** Le message de réussite, avec ce que le serveur dit avoir fait. */
  const messageReussite = (type, data, message) => {
    if (type === 'valider' || type === 'reporter') {
      const comptes = data?.rapport?.comptes || {};
      const ecrites = nombre(comptes.insere) + nombre(comptes.mis_a_jour);
      const conflits = nombre(data?.rapport?.conflits);
      const debut = type === 'valider' ? 'Feuille validée' : 'Report refait';
      return `${debut} : ${pluriel(ecrites, 'note écrite', 'notes écrites')} au carnet`
        + (conflits > 0
          ? `, ${pluriel(conflits, 'élève non reporté', 'élèves non reportés')} (conflit avec le carnet, détaillé sur la page).`
          : '.');
    }
    if (type === 'renvoyer') return "Feuille renvoyée à l'enseignant, avec votre motif.";
    if (type === 'autoriser') {
      const deverrouillees = nombre(data?.deverrouillage?.notes);
      return "Rectification autorisée : l'enseignant peut corriger les élèves concernés, puis soumettre de nouveau."
        + (deverrouillees > 0 ? ` ${pluriel(deverrouillees, 'note du carnet déverrouillée', 'notes du carnet déverrouillées')}, inscrite${deverrouillees > 1 ? 's' : ''} à l'historique des déverrouillages.` : '');
    }
    if (type === 'refuser') return "Rectification refusée : la feuille reste validée, l'enseignant lira votre motif.";
    return message || 'Décision enregistrée.';
  };

  const executer = async () => {
    if (!dialogue || actionEnCours.current) return;
    const { type, demande: demandeVisee } = dialogue;
    if (motifExige && !motifPret) return;
    actionEnCours.current = true;
    setEnvoi(true);
    setErreurDialogue(null);
    try {
      const version = feuille.version;
      const texte = motif.trim();
      let reponse;
      if (type === 'valider') reponse = await validationNotes.valider(feuille.id, version);
      else if (type === 'reporter') reponse = await validationNotes.reporter(feuille.id, version);
      else if (type === 'renvoyer') reponse = await validationNotes.renvoyer(feuille.id, version, texte);
      else if (type === 'autoriser') reponse = await validationNotes.autoriserRectification(demandeVisee.id, version, texte);
      else reponse = await validationNotes.refuserRectification(demandeVisee.id, version, texte);
      setDialogue(null);
      setMotif('');
      setToast({ message: messageReussite(type, reponse?.data, reponse?.message), type: 'success' });
      // Le serveur rend la page relue dans la même transaction que la
      // décision : on la pose telle quelle. Une forme inattendue fait relire.
      const { rapport, deverrouillage, ...page } = reponse?.data || {};
      if (page.feuille && Array.isArray(page.eleves)) {
        demandeEnCours.current += 1;
        setDonnees(page);
      } else {
        await charger();
      }
    } catch (e) {
      if (ecranEnRetard(e)) {
        setDialogue(null);
        setToast({ message: e.message, type: 'error' });
        await charger();
      } else {
        setErreurDialogue(e.message);
      }
    } finally {
      actionEnCours.current = false;
      setEnvoi(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Rendu
  // ---------------------------------------------------------------------------

  const lienRetour = <BoutonRetour to={retour.adresse} onClick={revenir}>{retour.libelle}</BoutonRetour>;

  if (!donnees) {
    return (
      <div className="w-full p-6">
        {lienRetour}
        {erreur ? (
          <div role="alert" className="mt-5 flex flex-wrap items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span className="min-w-0 flex-1">{erreur.message}</span>
            {erreur.statut !== 404 && erreur.statut !== 403 && (
              <button type="button" onClick={charger} className="font-semibold underline">Réessayer</button>
            )}
          </div>
        ) : (
          <div role="status" className="mt-5 flex items-center justify-center gap-2 py-16 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin text-[#246990]" />
            Chargement de la feuille…
          </div>
        )}
      </div>
    );
  }

  const enseignant = nomEmploye(feuille.soumise_par) || nomEmploye(feuille.auteur) || "L'enseignant";
  const coefficient = lireNote(feuille.coefficient);
  const ecritsAuReport = prevu.ajouts + prevu.remplacements + prevu.effacements;
  // Ce qu'un nouveau report réglerait : les notes à écrire, et les élèves
  // dont le carnet porte désormais la même note (déverrouillés depuis) — le
  // report les note comme reportés et les retire des conflits. Les élèves
  // déjà reportés ne comptent pas : le report ne les touche pas.
  const aReprendre = ecritsAuReport + prevu.inchanges;
  const depublications = nombre(compteurs.depublications);

  // Pourquoi un bouton attendu n'est pas là : dit en clair, jamais deviné.
  const raisonSansDecision = (droitVoulu, cle) => {
    if (!anneeOuverte) return "Cette feuille appartient à une année qui n'est plus ouverte : aucune décision n'est possible.";
    if (estVrai(droits.est_auteur)) return 'Vous avez saisi ou soumis cette feuille : un autre membre de la direction doit décider.';
    if (!estVrai(droits[droitVoulu])) return `Vous pouvez lire cette feuille, mais pas en décider : il faut, dans le module Notes, la permission ${cle}.`;
    return null;
  };

  const bandeau = (() => {
    switch (feuille.statut) {
      case 'soumise':
        return {
          ton: 'border-[#246990]/20 bg-[#eef7fb] text-gray-700',
          texte: `Soumise par ${enseignant}${feuille.soumise_le ? ` le ${dateHeure(feuille.soumise_le)}` : ''} : la saisie attend une décision de la direction.`
            + (feuille.validee_le ? " Elle avait déjà été validée : seuls les élèves rouverts par la rectification ont pu changer, les autres sont déjà au carnet." : '')
            + (primaire ? ' Au primaire, elle se valide avec le mois de la classe, toutes les matières soumises ensemble, sur la page du mois ; elle se renvoie ici, avec un motif.' : '')
        };
      case 'renvoyee':
        return {
          ton: 'border-red-200 bg-red-50 text-red-800',
          texte: `Renvoyée à l'enseignant${feuille.renvoyee_le ? ` le ${dateHeure(feuille.renvoyee_le)}` : ''}${nomEmploye(feuille.renvoyee_par) ? ` par ${nomEmploye(feuille.renvoyee_par)}` : ''}. Il corrige, puis soumet de nouveau.`,
          motif: feuille.motif_renvoi
        };
      case 'validee':
        return {
          ton: 'border-emerald-200 bg-emerald-50 text-emerald-800',
          texte: `Validée${feuille.validee_le ? ` le ${dateHeure(feuille.validee_le)}` : ''}${nomEmploye(feuille.valide_par) ? ` par ${nomEmploye(feuille.valide_par)}` : ''} : les notes ont été reportées au carnet, non verrouillées. Le secrétariat les verrouille en enregistrant le carnet, ce qui calcule aussi moyennes, rangs et appréciations.`
        };
      case 'rectification_demandee':
        return {
          ton: 'border-amber-200 bg-amber-50 text-amber-900',
          texte: "L'enseignant demande à rectifier des notes de cette feuille déjà validée. La demande, son motif et son justificatif sont plus bas."
        };
      case 'rectification_autorisee':
        return {
          ton: 'border-[#319190]/30 bg-[#319190]/10 text-gray-800',
          texte: primaire
            ? "Rectification autorisée : l'enseignant corrige les élèves concernés, puis soumet de nouveau la feuille, qui reviendra sur la page du mois pour être validée."
            : "Rectification autorisée : l'enseignant corrige les élèves concernés, puis soumet de nouveau la feuille, qui reviendra ici pour être validée."
        };
      case 'brouillon':
        return {
          ton: 'border-gray-200 bg-gray-50 text-gray-700',
          texte: "L'enseignant n'a pas encore soumis cette feuille : sa saisie peut encore changer, elle n'est pas montrée avant la soumission. La grille donne les notes actuelles du carnet."
        };
      default:
        return null;
    }
  })();

  return (
    <div className="w-full space-y-4 p-6">
      {lienRetour}

      {erreur && (
        <div role="alert" className="flex flex-wrap items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span className="min-w-0 flex-1">La feuille n'a pas pu être rafraîchie : {erreur.message}</span>
          <button type="button" onClick={charger} className="font-semibold underline">Réessayer</button>
        </div>
      )}

      {/* En-tête : la feuille, l'enseignant, le statut (titre de page en gras, comme l'administration) */}
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-[240px] flex-1">
          <p className="text-xs text-gray-500">
            Carnet d'évaluations › Saisies des enseignants › {primaire ? 'Primaire' : 'Collège et lycée'}
          </p>
          <h1 className="mt-1 text-2xl font-bold leading-tight text-[#246990]">{intituleFeuille(feuille)}</h1>
          <p className="mt-0.5 text-sm text-gray-500">
            {[
              periodeFeuille(feuille),
              coefficient !== null
                ? (coefficient === 0 ? 'coefficient 0, hors moyenne' : `coefficient ${ecrireNote(coefficient)}`)
                : '',
              `notes sur ${ecrireNote(bareme.max)}`,
              feuille.annee?.nom || ''
            ].filter(Boolean).join(' · ')}
          </p>
          <p className="mt-1 text-sm text-gray-600">Saisie par {enseignant}</p>
        </div>
        <BadgeStatutFeuille statut={feuille.statut} libre />
      </header>

      {!anneeOuverte && (
        <p className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
          Cette feuille appartient à l'année {feuille.annee?.nom || 'précédente'}, qui n'est plus ouverte : elle se lit,
          aucune décision n'est plus possible.
        </p>
      )}

      {bandeau && (
        <div className={`rounded-lg border p-4 text-sm ${bandeau.ton}`}>
          <p className="leading-relaxed">{bandeau.texte}</p>
          {bandeau.motif && (
            <p className="mt-2 whitespace-pre-wrap break-words rounded-md border border-red-200 bg-white/70 p-3 text-sm text-gray-700">
              <span className="font-semibold">Motif du renvoi : </span>
              {bandeau.motif}
            </p>
          )}

          {/* Valider ou renvoyer une feuille soumise. Au primaire, jamais
              « Valider » ici : on valide le mois de la classe, sur sa page. */}
          {feuille.statut === 'soumise' && ((actions.valider && !primaire) || actions.renvoyer || adresseDuMois ? (
            <div className="mt-3 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              {actions.renvoyer && (
                <Button variant="outline" size="default" className={BOUTON_ACTION} disabled={envoi} onClick={() => ouvrir('renvoyer')}>
                  <Undo2 className="h-4 w-4" />
                  Renvoyer à l'enseignant
                </Button>
              )}
              {primaire && adresseDuMois && (
                // Venu de la page du mois : on y revient (historique), plutôt
                // que d'en empiler une seconde dont le retour ne mènerait plus
                // à la file ou au suivi d'où l'on était parti.
                <Link to={adresseDuMois} onClick={depuis === 'mois' ? revenir : undefined} className={LIEN_PRINCIPAL}>
                  <CalendarCheck className="h-4 w-4" aria-hidden="true" />
                  {/* `actions.valider_mois` du serveur : le droit VALIDER du primaire,
                      l'année ouverte ET l'employé qui n'est pas l'auteur (intégration N3).
                      Un serveur qui ne le rend pas : le droit seul, comme avant. */}
                  {(actions.valider_mois === undefined ? droits.valider : estVrai(actions.valider_mois))
                    ? 'Valider sur la page du mois'
                    : 'Ouvrir la page du mois'}
                </Link>
              )}
              {actions.valider && !primaire && (
                <Button size="default" className={`${BOUTON_ACTION} ${BOUTON_PRINCIPAL}`} disabled={envoi} onClick={() => ouvrir('valider')}>
                  <ClipboardCheck className="h-4 w-4" />
                  Valider et reporter au carnet
                </Button>
              )}
            </div>
          ) : null)}
          {feuille.statut === 'soumise' && !actions.renvoyer && !(actions.valider && !primaire)
            && raisonSansDecision('valider', cles.VALIDER) && (
            <p className="mt-2 text-xs text-gray-600">{raisonSansDecision('valider', cles.VALIDER)}</p>
          )}

          {/* Une feuille validée dont des élèves sont restés en conflit */}
          {feuille.statut === 'validee' && nombre(feuille.conflits) > 0 && (
            <div className="mt-3 rounded-md border border-amber-200 bg-white/70 p-3 text-xs leading-relaxed text-amber-900">
              <p>
                {pluriel(nombre(feuille.conflits), "élève n'a pas pu être reporté", "élèves n'ont pas pu être reportés")}
                {feuille.dernier_report_le ? ` au dernier report (${dateHeure(feuille.dernier_report_le)})` : ''}.
                {aReprendre > 0
                  ? ` ${pluriel(aReprendre, 'peut', 'peuvent')} l'être maintenant : refaire le report ne touche qu'eux.`
                  : (prevu.conflits > 0
                    ? " Aucun ne peut l'être pour l'instant : une fois leurs lignes réglées dans le carnet, relisez la feuille, puis refaites le report - il ne touche qu'eux."
                    : '')}
              </p>
              {actions.reporter ? (
                // Rien à écrire : le report ne ferait qu'ajouter une ligne vide
                // au journal. La page se relit après un déverrouillage.
                <div className="mt-2 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                  <Button variant="outline" size="default" className={BOUTON_ACTION} disabled={envoi || chargement} onClick={charger}>
                    <RefreshCw className="h-4 w-4" />
                    Relire la feuille
                  </Button>
                  <Button
                    size="default"
                    className={`${BOUTON_ACTION} ${BOUTON_PRINCIPAL}`}
                    disabled={envoi || chargement || aReprendre === 0}
                    onClick={() => ouvrir('reporter')}
                  >
                    <ClipboardCheck className="h-4 w-4" />
                    Refaire le report
                  </Button>
                </div>
              ) : (
                raisonSansDecision('valider', cles.VALIDER) && (
                  <p className="mt-1 text-gray-600">{raisonSansDecision('valider', cles.VALIDER)}</p>
                )
              )}
            </div>
          )}
        </div>
      )}

      {!brouillon && (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <CarteCompteur
            libelle="Élèves saisis"
            valeur={nombre(compteurs.saisis)}
            sur={nombre(compteurs.eleves)}
            note={prevu.absents > 0 ? `dont ${pluriel(prevu.absents, 'absent')}` : null}
          />
          <CarteCompteur
            libelle="Différentes du carnet"
            valeur={nombre(compteurs.ecarts)}
            ton={nombre(compteurs.ecarts) > 0 ? 'attention' : 'neutre'}
            note={nombre(compteurs.ecarts) > 0 ? 'Surlignées dans la grille' : 'Saisie et carnet concordent'}
          />
          <CarteCompteur
            libelle={feuille.statut === 'validee' ? 'À reporter de nouveau' : 'Écrites au carnet par la validation'}
            valeur={ecritsAuReport}
            note={prevu.remplacements + prevu.effacements > 0
              ? `dont ${[
                prevu.remplacements > 0 ? pluriel(prevu.remplacements, 'note remplacée', 'notes remplacées') : '',
                prevu.effacements > 0 ? pluriel(prevu.effacements, 'note effacée', 'notes effacées') : ''
              ].filter(Boolean).join(' et ')}`
              : (prevu.deja > 0 ? `${pluriel(prevu.deja, 'déjà reportée', 'déjà reportées')}` : null)}
          />
          {/* Les conflits à régler, comptés sur la prévision du serveur : une
              ligne qui porte déjà la note de la saisie, même verrouillée, n'en
              est pas un (« Déjà au carnet »). */}
          <CarteCompteur
            libelle="Conflits avec le carnet"
            valeur={prevu.conflits}
            ton={prevu.conflits > 0 ? 'attention' : 'neutre'}
            note={prevu.verrouillees > 0 ? `dont ${pluriel(prevu.verrouillees, 'ligne verrouillée', 'lignes verrouillées')}` : null}
          />
        </div>
      )}

      {/* La demande de rectification en attente : ce que l'enseignant demande, pourquoi, sa pièce */}
      {demandeEnAttente && (
        <section className="rounded-lg border border-amber-200 bg-white p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-gray-900">Demande de rectification</h2>
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-gray-500">
                {[demandeEnAttente.demandeur, demandeEnAttente.demandee_le ? `le ${dateHeure(demandeEnAttente.demandee_le)}` : '']
                  .filter(Boolean).join(', ')}
              </span>
              <BadgeStatutDemande statut={demandeEnAttente.statut} />
            </div>
          </div>
          {demandeEnAttente.motif && (
            <p className="mt-2 whitespace-pre-wrap break-words text-sm text-gray-700">{demandeEnAttente.motif}</p>
          )}
          <div className="mt-3">
            <p className="text-xs font-semibold text-gray-700">{pluriel(concernes.size, 'élève concerné', 'élèves concernés')}</p>
            <ul className="mt-1 flex flex-wrap gap-1.5">
              {(demandeEnAttente.eleves || []).map((id) => {
                const eleve = eleves.find((e) => String(e.id) === String(id));
                return (
                  <li key={id} className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-xs text-gray-700">
                    {nomDe(id)}
                    {eleve && <span className="tabular-nums text-gray-500">({estVrai(eleve.absent) ? 'absent' : noteOuTiret(eleve.valeur)})</span>}
                    {concernesSortis.includes(String(id)) && <span className="font-semibold text-red-700">· plus dans la classe</span>}
                    {eleve && estVerrouillee(eleve) && (
                      <>
                        <Lock aria-hidden="true" className="h-3 w-3 text-gray-500" />
                        <span className="sr-only">(verrouillée au carnet)</span>
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
          {Array.isArray(demandeEnAttente.pieces) && demandeEnAttente.pieces.length > 0 ? (
            <div className="mt-3 flex flex-wrap gap-2">
              {demandeEnAttente.pieces.map((p) => (
                <Button key={p.id} variant="outline" size="sm" className="gap-2" onClick={() => setPieceOuverte(p)}>
                  <Paperclip className="h-3.5 w-3.5" />
                  {[p.nom_fichier || 'Justificatif', tailleFichier(p.taille)].filter(Boolean).join(' · ')}
                </Button>
              ))}
            </div>
          ) : (
            <p className="mt-3 text-[11px] text-gray-500">Aucun justificatif joint.</p>
          )}
          {verrouilleesConcernees.length > 0 && (
            <p className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs leading-relaxed text-amber-900">
              {pluriel(verrouilleesConcernees.length, 'note concernée est verrouillée', 'notes concernées sont verrouillées')} dans
              le carnet. Autoriser la rectification {verrouilleesConcernees.length > 1 ? 'les' : 'la'} déverrouille, par la même
              règle que l'outil de déverrouillage du carnet : un motif inscrit à son historique, et passé le délai de réclamation,
              la permission NOTES.VERROUILLAGE.FORCE.
              {!peutDeverrouiller && ` Il faut pour cela la permission de déverrouillage (${CLES_VALIDATION.DEVERROUILLER}), que vous n'avez pas : seule une personne qui l'a peut autoriser cette rectification.`}
            </p>
          )}
          {actions.autoriser || actions.refuser ? (
            <div className="mt-3 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              {actions.refuser && (
                <Button variant="outline" size="default" className={BOUTON_ACTION} disabled={envoi} onClick={() => ouvrir('refuser', demandeEnAttente)}>
                  <XCircle className="h-4 w-4" />
                  Refuser
                </Button>
              )}
              {actions.autoriser && (
                <Button
                  size="default"
                  className={`${BOUTON_ACTION} ${BOUTON_PRINCIPAL}`}
                  disabled={envoi || autorisationBloquee}
                  onClick={() => ouvrir('autoriser', demandeEnAttente)}
                >
                  <FilePen className="h-4 w-4" />
                  Autoriser la rectification
                </Button>
              )}
            </div>
          ) : (
            raisonSansDecision('rectifier', cles.RECTIFIER) && (
              <p className="mt-3 text-xs text-gray-600">{raisonSansDecision('rectifier', cles.RECTIFIER)}</p>
            )
          )}
        </section>
      )}

      {/* Les conflits : ce que le report n'écrit pas, et pourquoi */}
      {!brouillon && prevu.conflits > 0 && (
        <section className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <h2 className="text-sm font-semibold">
            {pluriel(prevu.conflits, 'élève ne sera pas reporté', 'élèves ne seront pas reportés')} au carnet
          </h2>
          <p className="mt-1 text-xs leading-relaxed">
            Le report n'écrase jamais une note du carnet verrouillée, venue d'un autre site ou modifiée depuis le dernier
            report : la note du carnet reste, et la ligne est signalée ici.
          </p>
          {/* Un carnet déjà enregistré verrouille toute la classe : la liste
              peut compter tous les élèves. Les premiers ici, le reste dans la
              grille, filtrée. */}
          <ul className="mt-2 space-y-1 text-xs">
            {eleves.filter(estConflit).slice(0, CONFLITS_LISTES).map((e) => (
              <li key={e.id} className="flex flex-wrap gap-x-2">
                <span className="font-semibold">{nomEleve(e) || nomDe(e.id)}</span>
                <span>- {e.prevision.explication || lecturePrevision(e).libelle}</span>
              </li>
            ))}
          </ul>
          {prevu.conflits > CONFLITS_LISTES && (
            <button
              type="button"
              onClick={() => setFiltre('conflits')}
              className="mt-1.5 text-xs font-semibold text-[#246990] hover:underline"
            >
              Voir les {prevu.conflits} élèves en conflit dans la grille
            </button>
          )}
          {prevu.verrouillees > 0 && (
            <div className="mt-3 rounded-md border border-amber-200 bg-white/70 p-3 text-xs leading-relaxed">
              <p>
                {pluriel(prevu.verrouillees, 'ligne est verrouillée', 'lignes sont verrouillées')} dans le carnet. Pour y mettre
                la note de l'enseignant, déverrouillez-{prevu.verrouillees > 1 ? 'les' : 'la'} d'abord ({CHEMIN_DEVERROUILLAGE},
                avec un motif ; passé le délai de réclamation, la permission NOTES.VERROUILLAGE.FORCE), puis
                {feuille.statut === 'validee'
                  ? ' refaites le report.'
                  : (primaire ? ' validez le mois : la validation les reportera.' : ' validez : la validation les reportera.')}
              </p>
              {!peutDeverrouiller && (
                <p className="mt-1 text-amber-800">
                  Vous n'avez pas la permission de déverrouillage ({CLES_VALIDATION.DEVERROUILLER}) : adressez-vous à une
                  personne qui l'a.
                </p>
              )}
              {peutDeverrouiller && outilsOuverts && (
                <Link to="/administration/configuration" className="mt-1 inline-block font-semibold text-[#246990] hover:underline">
                  Ouvrir les outils administratifs
                </Link>
              )}
            </div>
          )}
          {nombre(prevu.parMotif.transferee) > 0 && (
            <p className="mt-2 text-xs leading-relaxed">
              Une note venue d'un autre site avec l'élève n'est jamais remplacée par un report : si elle doit changer,
              corrigez-la dans le carnet.
            </p>
          )}
          {nombre(prevu.parMotif.carnet_modifie) > 0 && (
            <p className="mt-2 text-xs leading-relaxed">
              Une note modifiée dans le carnet depuis le dernier report n'est pas écrasée : si la saisie de l'enseignant
              doit l'emporter, corrigez-la dans le carnet.
            </p>
          )}
        </section>
      )}

      {/* L'espace famille : une ligne neuve, non verrouillée, cache le trimestre (le mois, au primaire) */}
      {parentsOuverts && depublications > 0 && ['soumise', 'validee'].includes(feuille.statut) && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs leading-relaxed text-amber-900">
          Espace famille : {pluriel(depublications, 'élève a', 'élèves ont')} un {periodePubliee} déjà publié aux familles. Une
          note ajoutée au carnet sans être verrouillée le leur retire jusqu'au prochain enregistrement du carnet par le
          secrétariat (un {periodePubliee} n'est montré que lorsque toutes ses notes sont verrouillées).
        </p>
      )}

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_380px]">
        {/* La grille : saisie, carnet, report */}
        <div className="min-w-0 rounded-lg border border-gray-200 bg-white">
          <div className="flex flex-wrap items-center gap-1.5 border-b border-gray-200 px-4 py-3" role="group" aria-label="Élèves affichés">
            {filtresProposes.map((f) => {
              const actif = filtreActif === f.cle;
              return (
                <button
                  key={f.cle}
                  type="button"
                  aria-pressed={actif}
                  onClick={() => setFiltre(f.cle)}
                  className={classesFiltre(actif)}
                >
                  {f.libelle}
                </button>
              );
            })}
          </div>
          {!brouillon && (
            <p className="px-4 pt-3 text-[11px] text-gray-500">
              {feuille.statut === 'soumise'
                ? `La colonne « Report au carnet » dit ce que fera la validation${primaire ? ' du mois' : ''}, élève par élève.`
                : 'La colonne « Report au carnet » dit ce que le report a fait, et ce que ferait un nouveau report.'}
            </p>
          )}
          {eleves.length === 0 ? (
            <div className="p-4">
              <EtatVide compact titre="Aucun élève sur cette feuille" description="La classe n'a aucun élève actif cette année, et la feuille n'a aucune ligne." />
            </div>
          ) : elevesFiltres.length === 0 ? (
            <div className="p-4">
              <EtatVide
                compact
                titre="Aucun élève dans ce filtre"
                action={<Button size="sm" variant="outline" onClick={() => setFiltre('tous')}>Voir tous les élèves</Button>}
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[680px] text-sm">
                <caption className="sr-only">
                  Notes de {intituleFeuille(feuille)}, {periodeFeuille(feuille)} : saisie de l'enseignant, note actuelle du carnet
                  et report prévu
                </caption>
                <thead>
                  <tr className="border-b border-gray-100 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                    <th scope="col" className="px-4 py-2.5">Élève</th>
                    <th scope="col" className="w-[140px] px-4 py-2.5">Saisie de l'enseignant</th>
                    <th scope="col" className="w-[150px] px-4 py-2.5">Carnet actuel</th>
                    <th scope="col" className="w-[230px] px-4 py-2.5">Report au carnet</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {elevesFiltres.map((e) => {
                    const carnet = lireNote(e.carnet?.valeur);
                    const verrouillee = estVerrouillee(e);
                    const transferee = estVrai(e.carnet?.transferee);
                    const conflit = estConflit(e);
                    const ecart = estVrai(e.ecart);
                    const concerne = concernes.has(String(e.id));
                    const lecture = lecturePrevision(e, { brouillon });
                    const reporte = e.reporte ? lireNote(e.reporte.valeur) : undefined;
                    const modifieeDepuis = e.reporte && reporte !== carnet && !(reporte === null && carnet === null);
                    return (
                      <tr key={e.id} className={conflit ? FOND_LIGNE.conflit : (ecart ? FOND_LIGNE.ecart : '')}>
                        <td className={`px-4 py-2.5 ${concerne ? 'border-l-4 border-amber-400' : ''}`}>
                          <p className="font-medium text-gray-900">{nomEleve(e) || nomDe(e.id)}</p>
                          <p className="text-[11px] text-gray-500">
                            {[
                              e.dans_classe === false ? "n'est plus dans la classe" : '',
                              concerne ? 'rectification demandée' : '',
                              rouverts.has(String(e.id)) ? 'rouvert par la rectification' : ''
                            ].filter(Boolean).join(' · ')}
                          </p>
                        </td>
                        <td className="px-4 py-2.5">
                          {brouillon ? (
                            <span className="text-gray-400">-</span>
                          ) : estVrai(e.absent) ? (
                            <span className="text-xs text-gray-600">Absent</span>
                          ) : lireNote(e.valeur) !== null ? (
                            <span className="font-semibold tabular-nums text-gray-900">{ecrireNote(e.valeur)}</span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5">
                          <span className="inline-flex items-center gap-1.5">
                            <span className={`tabular-nums ${carnet === null ? 'text-gray-400' : 'text-gray-800'}`}>{noteOuTiret(carnet)}</span>
                            {verrouillee && (
                              <>
                                <Lock aria-hidden="true" className="h-3.5 w-3.5 text-gray-500" />
                                <span className="sr-only">(verrouillée)</span>
                              </>
                            )}
                          </span>
                          {transferee && <p className="text-[11px] text-gray-500">venue d'un autre site</p>}
                          {nombre(e.lignes_mensuelles) > 0 && <p className="text-[11px] text-gray-500">note mensuelle au carnet</p>}
                          {nombre(e.doublons_mois) > 0 && <p className="text-[11px] text-gray-500">deux lignes au carnet ce mois</p>}
                          {modifieeDepuis && (
                            <p className="text-[11px] font-medium text-amber-800">
                              {noteOuTiret(reporte)} reporté{e.reporte.le ? ` le ${dateHeure(e.reporte.le)}` : ''}, modifiée depuis
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-2.5">
                          <p className={`text-xs ${TONS_PREVISION[lecture.ton] || TONS_PREVISION.neutre}`}>{lecture.libelle}</p>
                          {lecture.detail && <p className="text-[11px] leading-snug text-gray-600">{lecture.detail}</p>}
                          {estVrai(e.depublie) && parentsOuverts && (
                            <p className="text-[11px] font-medium text-amber-800">{periodePubliee} retiré à la famille jusqu'au prochain enregistrement</p>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Colonne de droite : demandes passées et journal */}
        <div className="space-y-4">
          {demandesPassees.length > 0 && (
            <section className="rounded-lg border border-gray-200 bg-white p-4">
              <h2 className="text-sm font-semibold text-gray-900">Demandes de rectification précédentes</h2>
              <ul className="mt-2 divide-y divide-gray-100">
                {demandesPassees.map((d) => (
                  <li key={d.id} className="py-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-[11px] text-gray-500">
                        {[d.demandeur, d.demandee_le ? `le ${dateHeure(d.demandee_le)}` : ''].filter(Boolean).join(', ') || 'Demande'}
                      </span>
                      <BadgeStatutDemande statut={d.statut} />
                    </div>
                    {d.motif && <p className="mt-1.5 whitespace-pre-wrap break-words text-xs text-gray-700">{d.motif}</p>}
                    {Array.isArray(d.eleves) && d.eleves.length > 0 && (
                      <p className="mt-1 text-[11px] text-gray-500">{d.eleves.map(nomDe).join(', ')}</p>
                    )}
                    {(d.decideur || d.decidee_le || d.decision_motif) && (
                      <p className="mt-1.5 whitespace-pre-wrap break-words rounded-md bg-gray-50 p-2 text-xs text-gray-700">
                        <span className="font-semibold">
                          {[d.decideur, d.decidee_le ? `le ${dateHeure(d.decidee_le)}` : ''].filter(Boolean).join(', ') || 'Décision'}
                          {d.decision_motif ? ' : ' : ''}
                        </span>
                        {d.decision_motif}
                      </p>
                    )}
                    {nombre(d.notes_deverrouillees) > 0 && (
                      <p className="mt-1 text-[11px] text-gray-500">
                        {pluriel(nombre(d.notes_deverrouillees), 'note du carnet déverrouillée', 'notes du carnet déverrouillées')}
                      </p>
                    )}
                    {Array.isArray(d.pieces) && d.pieces.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {d.pieces.map((p) => (
                          <Button key={p.id} variant="outline" size="sm" className="gap-2" onClick={() => setPieceOuverte(p)}>
                            <Paperclip className="h-3.5 w-3.5" />
                            {p.nom_fichier || 'Justificatif'}
                          </Button>
                        ))}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="rounded-lg border border-gray-200 bg-white p-4">
            <h2 className="text-sm font-semibold text-gray-900">Journal de la feuille</h2>
            {journal.length === 0 ? (
              <p className="mt-2 text-xs text-gray-500">Aucun événement inscrit.</p>
            ) : (
              <ol className="mt-2 divide-y divide-gray-100">
                {journal.map((entree, index) => (
                  <EntreeJournal key={entree.id ?? index} entree={entree} nomDe={nomDe} />
                ))}
              </ol>
            )}
          </section>
        </div>
      </div>

      {/* Valider */}
      <ConfirmDialog
        isOpen={dialogue?.type === 'valider'}
        onClose={fermer}
        onConfirm={executer}
        isLoading={envoi}
        type="question"
        contentClassName="sm:max-w-xl"
        title="Valider et reporter au carnet ?"
        description={`${intituleFeuille(feuille)} - ${periodeFeuille(feuille)}, saisie par ${enseignant}.`}
        confirmText="Valider et reporter"
      >
        <div className="space-y-3 text-left text-sm text-gray-700">
          <ul className="space-y-1.5">
            <li>
              {pluriel(prevu.ajouts, 'note ajoutée', 'notes ajoutées')} au carnet, à la place de l'épreuve
              « {feuille.epreuve_libelle || periodeFeuille(feuille)} ».
            </li>
            {prevu.remplacements > 0 && (
              <li className="font-semibold text-amber-800">
                {pluriel(prevu.remplacements, 'note du carnet, non verrouillée, sera remplacée', 'notes du carnet, non verrouillées, seront remplacées')} par
                la saisie de l'enseignant.
              </li>
            )}
            {prevu.effacements > 0 && (
              <li className="font-semibold text-amber-800">
                {pluriel(prevu.effacements, 'note du carnet sera effacée', 'notes du carnet seront effacées')} : reportée
                {prevu.effacements > 1 ? 's' : ''} par cette feuille, la saisie ne {prevu.effacements > 1 ? 'les' : 'la'} porte
                plus (élève absent ou case vide).
              </li>
            )}
            {prevu.inchanges + prevu.deja > 0 && (
              <li>{pluriel(prevu.inchanges + prevu.deja, 'élève sans changement', 'élèves sans changement')} au carnet.</li>
            )}
            {prevu.sansSaisie > 0 && (
              <li className="font-semibold text-amber-800">
                {pluriel(prevu.sansSaisie, 'élève de la classe n\'a', 'élèves de la classe n\'ont')} pas de note dans
                cette saisie : rien n'est écrit pour {prevu.sansSaisie > 1 ? 'eux' : 'lui'} au carnet.
              </li>
            )}
            {prevu.conflits > 0 && (
              <li className="font-semibold text-red-700">
                {pluriel(prevu.conflits, 'élève en conflit', 'élèves en conflit')} avec le carnet : rien n'est écrit pour
                {prevu.conflits > 1 ? ' eux' : ' lui'}, la note du carnet reste.
              </li>
            )}
          </ul>
          <p className="rounded-md border border-[#246990]/20 bg-[#eef7fb] p-3 text-xs leading-relaxed">
            Les notes arrivent au carnet non verrouillées. La moyenne générale, les rangs et les appréciations ne sont
            calculés qu'à l'enregistrement du carnet par le secrétariat, qui verrouille alors ces notes.
          </p>
          {parentsOuverts && depublications > 0 && (
            <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs leading-relaxed text-amber-900">
              Espace famille : pour {pluriel(depublications, 'élève', 'élèves')}, ce {periodePubliee} est déjà publié ; il sera
              retiré aux familles jusqu'au prochain enregistrement du carnet.
            </p>
          )}
          {erreurDialogue && <p role="alert" className="text-sm text-red-700">{erreurDialogue}</p>}
        </div>
      </ConfirmDialog>

      {/* Refaire le report */}
      <ConfirmDialog
        isOpen={dialogue?.type === 'reporter'}
        onClose={fermer}
        onConfirm={executer}
        isLoading={envoi}
        type="question"
        contentClassName="sm:max-w-xl"
        title="Refaire le report ?"
        description="Seuls les élèves restés en conflit sont repris ; ceux déjà reportés ne bougent pas."
        confirmText="Refaire le report"
      >
        <div className="space-y-3 text-left text-sm text-gray-700">
          <ul className="space-y-1.5">
            <li>
              {pluriel(prevu.ajouts + prevu.remplacements, 'note sera écrite', 'notes seront écrites')} au carnet, non
              verrouillée{prevu.ajouts + prevu.remplacements > 1 ? 's' : ''}.
            </li>
            {prevu.effacements > 0 && (
              <li className="font-semibold text-amber-800">
                {pluriel(prevu.effacements, 'note du carnet sera effacée', 'notes du carnet seront effacées')} (élève absent
                ou case vide dans la saisie).
              </li>
            )}
            {prevu.inchanges > 0 && (
              <li>
                {pluriel(prevu.inchanges, "élève n'a", "élèves n'ont")} rien à écrire, le carnet portant déjà la saisie :
                {prevu.inchanges > 1 ? ' ils sortent' : ' il sort'} des conflits.
              </li>
            )}
            {prevu.conflits > 0 && (
              <li className="font-semibold text-red-700">
                {pluriel(prevu.conflits, 'élève reste', 'élèves restent')} en conflit : rien n'est écrit pour
                {prevu.conflits > 1 ? ' eux' : ' lui'}.
              </li>
            )}
          </ul>
          {parentsOuverts && depublications > 0 && (
            <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs leading-relaxed text-amber-900">
              Espace famille : pour {pluriel(depublications, 'élève', 'élèves')}, ce {periodePubliee} est déjà publié ; il sera
              retiré aux familles jusqu'au prochain enregistrement du carnet.
            </p>
          )}
          {erreurDialogue && <p role="alert" className="text-sm text-red-700">{erreurDialogue}</p>}
        </div>
      </ConfirmDialog>

      {/* Renvoyer */}
      <ConfirmDialog
        isOpen={dialogue?.type === 'renvoyer'}
        onClose={fermer}
        onConfirm={executer}
        isLoading={envoi}
        type="warning"
        contentClassName="sm:max-w-xl"
        title="Renvoyer la feuille à l'enseignant ?"
        description={`${enseignant} pourra corriger ${intituleFeuille(feuille)} (${periodeFeuille(feuille)}) puis la soumettre de nouveau. Rien n'est écrit dans le carnet.`}
        confirmText="Renvoyer"
        confirmDisabled={!motifPret}
      >
        <div className="space-y-2">
          <ChampMotif
            id={idMotif}
            valeur={motif}
            onChange={setMotif}
            requis
            libelle="Motif, lu par l'enseignant"
            aide="Dites ce qui doit être corrigé."
          />
          {erreurDialogue && <p role="alert" className="text-sm text-red-700">{erreurDialogue}</p>}
        </div>
      </ConfirmDialog>

      {/* Autoriser une rectification */}
      <ConfirmDialog
        isOpen={dialogue?.type === 'autoriser'}
        onClose={fermer}
        onConfirm={executer}
        isLoading={envoi}
        type="question"
        contentClassName="sm:max-w-xl"
        title="Autoriser la rectification ?"
        description={`La feuille rouvre pour ${pluriel(concernes.size, 'élève', 'élèves')} seulement ; l'enseignant corrige puis la soumet de nouveau, et elle reviendra ${primaire ? 'sur la page du mois' : 'ici'} pour être validée.`}
        confirmText="Autoriser"
        confirmDisabled={(motifExige && !motifPret) || autorisationBloquee || concernesSortis.length > 0}
      >
        <div className="space-y-3 text-left text-sm text-gray-700">
          <p className="text-xs text-gray-600">
            <span className="font-semibold">Élèves : </span>
            {(dialogue?.demande?.eleves || []).map(nomDe).join(', ') || '-'}
          </p>
          {concernesSortis.length > 0 && (
            <p role="alert" className="rounded-md border border-red-200 bg-red-50 p-3 text-xs leading-relaxed text-red-700">
              {concernesSortis.length > 1
                ? `Ces élèves ne sont plus dans la classe : ${concernesSortis.map(nomDe).join(', ')}. La rectification serait sans effet pour eux : refusez cette demande ; l'enseignant pourra en faire une nouvelle sans eux.`
                : `${nomDe(concernesSortis[0])} n'est plus dans la classe : la rectification serait sans effet pour cet élève. Refusez cette demande ; l'enseignant pourra en faire une nouvelle sans cet élève.`}
            </p>
          )}
          {verrouilleesConcernees.length > 0 && (
            <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs leading-relaxed text-amber-900">
              {pluriel(verrouilleesConcernees.length, 'note est verrouillée', 'notes sont verrouillées')} dans le carnet :
              {verrouilleesConcernees.length > 1 ? ' elles seront déverrouillées' : ' elle sera déverrouillée'} avec ce motif,
              inscrit à l'historique des déverrouillages. Passé le délai de réclamation, il faut aussi la permission
              NOTES.VERROUILLAGE.FORCE ; sans elle, rien n'est fait.
            </p>
          )}
          <ChampMotif
            id={`${idMotif}-autoriser`}
            valeur={motif}
            onChange={setMotif}
            requis={verrouilleesConcernees.length > 0}
            libelle={verrouilleesConcernees.length > 0 ? 'Motif du déverrouillage' : "Commentaire pour l'enseignant (facultatif)"}
            aide={verrouilleesConcernees.length > 0 ? "Inscrit à l'historique des déverrouillages et lu par l'enseignant." : "Lu par l'enseignant."}
          />
          {erreurDialogue && <p role="alert" className="text-sm text-red-700">{erreurDialogue}</p>}
        </div>
      </ConfirmDialog>

      {/* Refuser une rectification */}
      <ConfirmDialog
        isOpen={dialogue?.type === 'refuser'}
        onClose={fermer}
        onConfirm={executer}
        isLoading={envoi}
        type="warning"
        contentClassName="sm:max-w-xl"
        title="Refuser la rectification ?"
        description="La feuille reste validée telle quelle ; l'enseignant lira votre motif."
        confirmText="Refuser"
        confirmDisabled={!motifPret}
      >
        <div className="space-y-2">
          <ChampMotif
            id={`${idMotif}-refuser`}
            valeur={motif}
            onChange={setMotif}
            requis
            libelle="Motif du refus, lu par l'enseignant"
          />
          {erreurDialogue && <p role="alert" className="text-sm text-red-700">{erreurDialogue}</p>}
        </div>
      </ConfirmDialog>

      <VisionneusePiece piece={pieceOuverte} onFermer={() => setPieceOuverte(null)} />

      {toast && (
        <ToastPortal>
          {shouldUseLongToast(toast.message, toast.type, toast) ? (
            <LongToast show message={toast.message} type={toast.type} onClose={() => setToast(null)} />
          ) : (
            <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
          )}
        </ToastPortal>
      )}
    </div>
  );
};

// ----------------------------------------------------------------------------
// Une entrée du journal, avec la photographie que le serveur y a rangée :
// les valeurs soumises, celles d'une demande de rectification, le détail d'un
// report. C'est elle qui montre « avant / après » une rectification.
// ----------------------------------------------------------------------------

const RESULTATS_REPORT = {
  insere: 'ajoutée',
  mis_a_jour: 'remplacée',
  conflit: 'non reportée'
};

function EntreeJournal({ entree, nomDe }) {
  const details = detailsJournal(entree.details);
  const motif = typeof details?.motif === 'string' ? details.motif : null;

  let photographie = null;
  const valeursSaisies = (liste) => liste.map((v, i) => ({
    cle: `${v.eleve_id}-${i}`,
    nom: nomDe(v.eleve_id),
    valeur: estVrai(v.absent) ? 'Absent' : noteOuTiret(v.valeur)
  }));
  if (entree.action === 'soumission' && Array.isArray(details?.valeurs) && details.valeurs.length) {
    photographie = { titre: 'Valeurs soumises', lignes: valeursSaisies(details.valeurs) };
  } else if (entree.action === 'rectification_demandee' && Array.isArray(details?.eleves) && details.eleves.length) {
    photographie = { titre: 'Valeurs au moment de la demande', lignes: valeursSaisies(details.eleves) };
  } else if (['validation', 'report'].includes(entree.action) && Array.isArray(details?.resultats)) {
    // Seuls les élèves dont le carnet a changé, ou qui n'ont pas pu l'être.
    const lignes = details.resultats
      .filter((r) => RESULTATS_REPORT[r.resultat])
      .map((r, i) => ({
        cle: `${r.eleve_id}-${i}`,
        nom: nomDe(r.eleve_id),
        valeur: r.resultat === 'conflit'
          ? RESULTATS_REPORT.conflit
          : `${noteOuTiret(r.avant)} → ${noteOuTiret(r.apres)}`,
        ton: r.resultat === 'conflit' ? 'text-red-700' : ''
      }));
    if (lignes.length) photographie = { titre: 'Détail du report', lignes };
  }

  return (
    <li className="py-2.5">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3">
        <p className="text-sm font-semibold text-gray-800">{libelleAction(entree.action)}</p>
        <p className="text-[11px] text-gray-500">{dateHeure(entree.le)}</p>
      </div>
      {acteurJournal(entree) && <p className="text-xs text-gray-500">{acteurJournal(entree)}</p>}
      {entree.resume && <p className="mt-1 whitespace-pre-wrap break-words text-xs text-gray-700">{entree.resume}</p>}
      {motif && (
        <p className="mt-1 whitespace-pre-wrap break-words rounded-md bg-gray-50 p-2 text-xs text-gray-700">
          <span className="font-semibold">Motif : </span>
          {motif}
        </p>
      )}
      {photographie && <ValeursRepliees titre={photographie.titre} lignes={photographie.lignes} />}
    </li>
  );
}

export default PageFeuilleNotes;
